import { Input } from "@/components/ui/input";
import { SingleDatePicker } from "@/components/ui/library/single-date-picker";
import { cn } from "@/lib/utils";
import {
  formatCfDate,
  isFormattedCfType,
  isValidYmd,
  resolveCfDateMax,
  sanitizeCfNumberInput,
} from "../utils/cfValueTypes";

export interface CfValueInputField {
  type?: string | null;
  name?: string | null;
  label?: string | null;
  dateMax?: string | null;
}

interface CfValueInputProps {
  field: CfValueInputField;
  /** The stored text: "1200", "45.5", "2026-10-31", or "". */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Classes for the text box (Number / Percentage) or the date box. */
  className?: string;
  invalid?: boolean;
  id?: string;
}

/**
 * The input for a NUMBERS, PERCENTAGE or DATE custom field — one widget so
 * the Add Lead form, the lead drawer, deals and bulk update all type and
 * limit the value the same way. Renders nothing for any other type: callers
 * keep their own text / remark / selector inputs exactly as they were.
 *
 * Date: a calendar that can't go past the field's `dateMax` (today, or a
 * fixed day), with month + year dropdowns for dates far from today.
 */
export function CfValueInput({
  field,
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
  invalid = false,
  id,
}: CfValueInputProps) {
  const type = String(field.type ?? "");
  if (!isFormattedCfType(type)) return null;
  const text = value == null ? "" : String(value);
  const invalidClass = invalid ? "border-red-500 focus-visible:ring-red-500" : "";

  if (type === "DATE") {
    if (disabled) {
      return (
        <Input
          id={id}
          readOnly
          disabled
          value={text ? formatCfDate(text) : ""}
          placeholder={placeholder ?? "dd / mm / yyyy"}
          className={cn(className, invalidClass)}
        />
      );
    }
    return (
      <SingleDatePicker
        id={id}
        // A legacy value the calendar can't read shows empty rather than a
        // half-formatted string; the stored text is untouched until a pick.
        date={isValidYmd(text) ? text : ""}
        onDateChange={onChange}
        maxDate={resolveCfDateMax(field.dateMax)}
        onClear={() => onChange("")}
        placeholder={placeholder ?? "dd / mm / yyyy"}
        inputClassName={cn(className, invalidClass)}
        yearPicker
      />
    );
  }

  const isPercent = type === "PERCENTAGE";
  return (
    <div className="relative w-full">
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        value={text}
        placeholder={placeholder ?? (isPercent ? "Eg. 72.5" : "Enter a number")}
        onChange={(e) => onChange(sanitizeCfNumberInput(e.target.value, !isPercent))}
        aria-invalid={invalid || undefined}
        className={cn(className, isPercent && "pr-8", invalidClass)}
      />
      {isPercent && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#697571]">
          %
        </span>
      )}
    </div>
  );
}

export default CfValueInput;
